import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const docs = {
  privacy: {
    title: "Privacy Policy",
    updated: "Effective: Jan 1, 2026",
    body: `CanHireSolution.com Inc. ("we", "our", "us") operates the CanHireSolution.com marketplace in Canada. This Privacy Policy describes how we collect, use, and disclose information.

1. Information We Collect
We collect information you provide when you sign up (name, email, photo via Google OAuth), build your profile, post jobs, and communicate with other users. We collect device and usage data for security and analytics.

2. How We Use Information
We use your information to operate the marketplace, match customers with workers, process payments, prevent fraud, and comply with Canadian laws (including PIPEDA).

3. Sharing
We share information with service providers (Stripe for payments, Google for authentication, our cloud hosting), with other users as necessary to deliver services, and as required by law.

4. Your Rights
You may access, correct, or delete your data at any time in Settings. We retain data only as long as necessary for legitimate business or legal reasons.

5. Security
We use industry-standard encryption in transit and at rest. No system is 100% secure; promptly report any concerns.

6. Children
CanHireSolution.com is not directed at children under 16.

7. Contact
Email privacy@quickjobs.ca for any questions or to exercise your rights.`,
  },
  terms: {
    title: "Terms of Service",
    updated: "Effective: Jan 1, 2026",
    body: `By using CanHireSolution.com you agree to these Terms.

1. Eligibility
You must be 18+ and legally able to provide or receive services in Canada.

2. Marketplace Role
CanHireSolution.com is a marketplace connecting customers with independent service providers. We are not the employer of any worker. See our Independent Contractor Disclaimer.

3. User Conduct
No fraud, harassment, off-platform circumvention, discrimination, or illegal activities. Violations may result in suspension or banning.

4. Payments
Some features require payment. All amounts are in CAD. Subscriptions auto-renew until canceled.

5. Content
You retain rights to content you post; you grant CanHireSolution.com a license to display it. We may remove content that violates these Terms.

6. Liability
Service is provided "as-is". To the maximum extent permitted by law, CanHireSolution.com is not liable for indirect or consequential damages.

7. Governing Law
These Terms are governed by the laws of the Province of Ontario, Canada.`,
  },
  refund: {
    title: "Refund Policy",
    updated: "Effective: Jan 1, 2026",
    body: `1. Subscriptions
Monthly subscriptions are non-refundable once billed. Cancel anytime to prevent the next charge.

2. Lead Unlocks
Single-lead unlocks (paid one-time purchases) are refundable if the customer becomes unresponsive within 48 hours after the worker reaches out in good faith. Submit refund requests via Contact Support with the session ID and screenshots.

3. Chargebacks
Chargebacks initiated without first contacting CanHireSolution.com may result in account suspension.

4. Processing
Approved refunds are issued to the original payment method within 5–10 business days.`,
  },
  community: {
    title: "Community Guidelines",
    updated: "Effective: Jan 1, 2026",
    body: `CanHireSolution.com thrives on trust. By using the platform you agree to:

• Be respectful. No harassment, hate speech, or discrimination.
• Be honest. No fake profiles, fake reviews, or misleading pricing.
• Stay on platform. Conducting transactions off-platform circumvents safety features and may result in suspension.
• Respect safety. No requests or offers for illegal services. Comply with local licensing.
• Privacy. Don't share personal contact info publicly. Use in-app chat.
• Reviews. Reviews must be truthful and based on first-hand experience.

Violations may result in content removal, suspension, or permanent banning. Report violations via the in-app Report function.`,
  },
  contractor: {
    title: "Independent Contractor Disclaimer",
    updated: "Effective: Jan 1, 2026",
    body: `CanHireSolution.com is a technology marketplace. Workers who provide services through CanHireSolution.com are independent contractors and not employees, agents, partners, or franchisees of CanHireSolution.com.

Independent contractors are responsible for:
• Their own licensing, insurance, and regulatory compliance.
• Reporting and remitting all applicable taxes (GST/HST, provincial taxes, income tax).
• The quality, safety, and legality of services provided.
• Tools, equipment, transportation, and supplies.

Customers acknowledge that CanHireSolution.com does not warrant, endorse, or guarantee any worker's services. Always verify licenses, insurance, and references for sensitive work.`,
  },
};

export default function Legal({ slug }) {
  const doc = docs[slug];
  if (!doc) return null;
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <article className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-2">{doc.title}</h1>
        <div className="text-sm text-slate-500 mb-8">{doc.updated}</div>
        <div className="prose prose-slate max-w-none">
          {doc.body.split("\n\n").map((p, i) => (
            <p key={`${slug}-${i}-${p.slice(0, 12)}`} className="text-base text-slate-700 leading-relaxed whitespace-pre-line mb-4">{p}</p>
          ))}
        </div>
      </article>
      <Footer />
    </div>
  );
}
